import { Skeleton, Text, VStack } from 'ui_2';
import {
  useUserCurrentGiveawaysQuery,
  useUserPastGiveawaysQuery,
} from '@src/hooks/useGiveaways';
import { RaffleV2 } from 'jigsaw';
import { TRPCClientErrorLike } from '@trpc/client';
import { GiveawayHistoryRow } from './GiveawayHistoryRow';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import { ConnectButton } from '../ConnectOrAccount';

const GiveawayHistoryError = ({ errorMessage }: { errorMessage?: string }) => {
  return (
    <VStack className='w-full items-center'>
      <Text>Error loading giveaway history.</Text>
      {errorMessage && <Text>{errorMessage}</Text>}
    </VStack>
  );
};

const GiveawayHistoryLoading = ({
  skeletonCount = 2,
}: {
  skeletonCount?: number;
}) => {
  return (
    <VStack className='w-full items-center gap-2'>
      {[...Array(skeletonCount).keys()].map((_, ix) => (
        <Skeleton className='h-20 w-full max-w-[500px] ' delay={ix} key={ix} />
      ))}
    </VStack>
  );
};

const GiveawayHistoryEmpty = ({ section }: { section: 'current' | 'past' }) => {
  return (
    <VStack className='w-full items-center'>
      <Text>{'No ' + section + ' giveaways.'}</Text>
    </VStack>
  );
};

type GiveawaysProps = {
  isLoading: boolean;
  isError: boolean;
  section: 'current' | 'past';
  error: TRPCClientErrorLike<any> | null;
  giveaways?: RaffleV2[];
  skeletonCount?: number;
};

const GiveawayHistory = ({
  isLoading,
  isError,
  section,
  error,
  giveaways,
  skeletonCount,
}: GiveawaysProps) => {
  const { account } = useArcadeStoreState();
  if (!account) {
    return (
      <VStack className='items-center gap-1'>
        <Text>Connect Puzzle Wallet first.</Text>
        <ConnectButton />
      </VStack>
    );
  }

  if (isError) {
    return <GiveawayHistoryError errorMessage={error?.message} />;
  } else if (isLoading || !giveaways) {
    return <GiveawayHistoryLoading skeletonCount={skeletonCount} />;
  } else if (giveaways.length === 0) {
    return <GiveawayHistoryEmpty section={section} />;
  } else {
    return (
      <VStack className='w-full items-center gap-2'>
        {giveaways.map((giveaway, ix) => (
          <GiveawayHistoryRow giveaway={giveaway} key={ix} />
        ))}
      </VStack>
    );
  }
};

export const CurrentGiveawayHistory = () => {
  const { data, error, isError, isLoading } = useUserCurrentGiveawaysQuery();

  return (
    <GiveawayHistory
      isError={isError}
      isLoading={isLoading}
      section={'current'}
      error={error}
      giveaways={data}
      skeletonCount={4}
    />
  );
};

export const PastGiveawayHistory = () => {
  const { data, error, isError, isLoading } = useUserPastGiveawaysQuery();

  return (
    <GiveawayHistory
      isError={isError}
      isLoading={isLoading}
      section={'past'}
      error={error}
      giveaways={data}
      skeletonCount={4}
    />
  );
};
